import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { 
  Menu, 
  X, 
  Phone, 
  Search, 
  Globe,
  LogIn
} from 'lucide-react';
import { useLanguageStore, languages } from '../store/useLanguageStore';
import { NavigationItem, Language } from '../types';
import logo from '../assets/images/logo.jpg';

const Header: React.FC = () => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { currentLanguage, setLanguage, t } = useLanguageStore();

  const navigationItems: NavigationItem[] = [
    { id: 'home', label: 'Home', labelSinhala: 'මුල් පිටුව', href: '/' },
    { id: 'services', label: 'Services', labelSinhala: 'සේවාවන්', href: '/services' },
    { id: 'units', label: 'Units', labelSinhala: 'ඒකක', href: '/units' },
    { id: 'hierarchy', label: 'Hierarchy', labelSinhala: 'ධුරාවලිය', href: '/hierarchy' },
  ];

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const toggleSearch = () => setIsSearchOpen(!isSearchOpen);

  // Prevent background scroll when the mobile menu is open
  useEffect(() => {
    if (!isMenuOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isMenuOpen]);

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-custom shadow-lg border-b border-primary-green/10">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Emergency Banner - Clickable Phone Number */}
        <a 
          href="tel:+94472246260"
          className="bg-error-red text-white py-1.5 sm:py-2 px-3 sm:px-4 text-center text-xs sm:text-sm font-medium block hover:bg-red-700 transition-colors duration-300 cursor-pointer"
          aria-label="Call emergency number"
        >
          <div className="flex items-center justify-center gap-1.5 sm:gap-2">
            <Phone className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 animate-pulse" />
            <span className="truncate font-semibold">
              {t('emergency', 'Emergency: +94 47 224 6260', 'හදිසි: +94 47 224 6260')}
            </span>
          </div>
        </a>

        <div className="flex items-center justify-between py-3 sm:py-4">
          {/* Logo Section */}
          <motion.div 
            className="flex items-center gap-2 sm:gap-3 md:gap-4"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="relative">
              <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full overflow-hidden border-2 border-primary-green shadow-lg bg-white p-1">
                <img 
                  src={logo} 
                  alt="Government Hospital Logo" 
                  className="w-full h-full object-contain rounded-full"
                />
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 sm:-bottom-1 sm:-right-1 w-3 h-3 sm:w-4 sm:h-4 bg-gradient-to-br from-orange-500 via-primary-green to-red-600 rounded-full border-2 border-white shadow-sm"></div>
            </div>
            <div className="flex flex-col min-w-0">
              <div className="flex items-center gap-2 mb-0.5 sm:mb-1">
                <h1 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-text-dark leading-tight truncate">
                  {t('hospital-name', 'Divisional Hospital', 'ප්‍රාදේශීය රෝහල')}
                </h1>
              </div>
              <p className="text-xs sm:text-sm md:text-base text-text-secondary font-medium truncate">
                {t('location', 'Weeraketiya', 'වීරකැටිය')}
              </p>
            </div>
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navigationItems.map((item, index) => (
              <motion.div key={item.id} initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }}>
                <Link to={item.href} className={`font-medium transition-colors duration-300 relative group ${location.pathname === item.href ? 'text-primary-green' : 'text-text-dark hover:text-primary-green'}`}>
                  {currentLanguage.code === 'si' ? item.labelSinhala : item.label}
                </Link>
              </motion.div>
            ))}
          </nav>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
            <motion.button onClick={toggleSearch} className="p-1.5 sm:p-2 text-text-secondary hover:text-primary-green touch-manipulation">
              <Search className="w-4 h-4 sm:w-5 sm:h-5" />
            </motion.button>

            <div className="relative group">
              <motion.button className="flex items-center gap-1.5 sm:gap-2 p-1.5 sm:p-2 text-text-secondary hover:text-primary-green touch-manipulation">
                <Globe className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-xs sm:text-sm font-medium hidden sm:inline">{currentLanguage.nativeName}</span>
              </motion.button>
              <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-xl border border-gray-200 py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                {languages.map((lang: Language) => (
                  <button key={lang.code} onClick={() => setLanguage(lang)} className={`block w-full text-left px-4 py-2 hover:bg-gray-50 ${currentLanguage.code === lang.code ? 'text-primary-green font-medium' : 'text-text-dark'}`}>
                    {lang.nativeName}
                  </button>
                ))}
              </div>
            </div>

            {/* LOGIN BUTTON - Professional */}
            <Link to="/login">
              <motion.div
                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-primary-green to-secondary-blue text-white rounded-lg font-medium shadow-md hover:shadow-lg transition-all duration-300 border border-primary-green/20"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <LogIn className="w-4 h-4" />
                <span className="hidden sm:inline">{t('login', 'Login', 'ඇතුල් වන්න')}</span>
              </motion.div>
            </Link>

            <motion.a href="tel:+94472246260" className="btn-emergency hidden md:flex items-center gap-2 text-sm lg:text-base">
              <Phone className="w-3 h-3 lg:w-4 lg:h-4" />
              <span className="hidden lg:inline">{t('emergency-short', 'Emergency', 'හදිසි')}</span>
            </motion.a>

            <motion.button onClick={toggleMenu} className="lg:hidden p-1.5 sm:p-2 text-text-dark hover:text-primary-green touch-manipulation">
              {isMenuOpen ? <X className="w-5 h-5 sm:w-6 sm:h-6" /> : <Menu className="w-5 h-5 sm:w-6 sm:h-6" />}
            </motion.button>
          </div>
        </div>

        <AnimatePresence>
          {isSearchOpen && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="pb-3 sm:pb-4 border-t border-gray-100 pt-3 sm:pt-4">
              <div className="flex items-center gap-2 sm:gap-4 max-w-md mx-auto">
                <div className="flex-1 relative">
                  <Search className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 text-text-secondary w-4 h-4 sm:w-5 sm:h-5" />
                  <input type="text" placeholder={t('search-placeholder', 'Search services...', 'සේවාවන් සොයන්න...')} className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green" />
                </div>
                <button onClick={toggleSearch} className="p-2 sm:px-4 sm:py-3 text-text-secondary hover:text-primary-green touch-manipulation">
                  <X className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Mobile Menu Overlay (Portal) */}
      {isMenuOpen && typeof document !== 'undefined' && createPortal(
        <>
          {/* Backdrop */}
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.55)',
              zIndex: 1000
            }}
            onClick={() => setIsMenuOpen(false)}
            className="lg:hidden"
          />

          {/* Menu Panel */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              width: '320px',
              maxWidth: '90vw',
              backgroundColor: '#ffffff',
              boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
              zIndex: 1001,
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch'
            }}
            className="lg:hidden"
          >
            {/* Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px',
                backgroundColor: '#2E7D32',
                color: '#ffffff'
              }}
            >
              <span style={{ fontSize: '18px', fontWeight: 700 }}>
                {t('menu', 'Menu', 'මෙනුව')}
              </span>
              <button
                onClick={() => setIsMenuOpen(false)}
                style={{
                  padding: '8px',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(255,255,255,0.12)',
                  border: 'none',
                  cursor: 'pointer'
                }}
                aria-label="Close menu"
              >
                <X style={{ width: '24px', height: '24px', color: '#ffffff' }} />
              </button>
            </div>

            {/* Navigation Links */}
            <nav style={{ padding: '16px', backgroundColor: '#f9fafb' }}>
              {navigationItems.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.id}
                    to={item.href}
                    style={{
                      display: 'block',
                      padding: '14px 16px',
                      marginBottom: '10px',
                      borderRadius: '10px',
                      fontSize: '16px',
                      fontWeight: 600,
                      textDecoration: 'none',
                      backgroundColor: isActive ? '#2E7D32' : '#ffffff',
                      color: isActive ? '#ffffff' : '#111827',
                      border: isActive ? 'none' : '1px solid #e5e7eb',
                      boxShadow: isActive ? '0 10px 25px rgba(46,125,50,0.25)' : 'none'
                    }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {currentLanguage.code === 'si' ? item.labelSinhala : item.label}
                  </Link>
                );
              })}
            </nav>

            {/* Language Selector */}
            <div style={{ padding: '16px', borderTop: '1px solid #e5e7eb', backgroundColor: '#ffffff' }}>
              <p style={{ fontSize: '14px', fontWeight: 700, color: '#374151', marginBottom: '12px' }}>
                {t('language', 'Language', 'භාෂාව')}
              </p>
              <div style={{ display: 'flex', gap: '10px' }}>
                {languages.map((lang: Language) => (
                  <button
                    key={lang.code}
                    onClick={() => setLanguage(lang)}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      borderRadius: '10px',
                      fontSize: '14px',
                      fontWeight: 600,
                      border: '1px solid transparent',
                      cursor: 'pointer',
                      backgroundColor: currentLanguage.code === lang.code ? '#2E7D32' : '#f3f4f6',
                      color: currentLanguage.code === lang.code ? '#ffffff' : '#1f2937',
                      boxShadow: currentLanguage.code === lang.code ? '0 6px 16px rgba(46,125,50,0.25)' : 'none'
                    }}
                  >
                    {lang.nativeName}
                  </button>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ padding: '16px', borderTop: '1px solid #e5e7eb', backgroundColor: '#ffffff' }}>
              <Link
                to="/login"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  width: '100%',
                  padding: '14px 16px',
                  marginBottom: '12px',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: 700,
                  textDecoration: 'none',
                  backgroundColor: '#2E7D32',
                  color: '#ffffff',
                  boxShadow: '0 10px 25px rgba(46,125,50,0.25)'
                }}
                onClick={() => setIsMenuOpen(false)}
              >
                <LogIn style={{ width: '20px', height: '20px' }} />
                {t('login', 'Login', 'ඇතුල් වන්න')}
              </Link>

              <a
                href="tel:+94472246260"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  width: '100%',
                  padding: '14px 16px',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: 700,
                  textDecoration: 'none',
                  backgroundColor: '#dc2626',
                  color: '#ffffff',
                  boxShadow: '0 10px 25px rgba(220,38,38,0.25)'
                }}
                onClick={() => setIsMenuOpen(false)}
              >
                <Phone style={{ width: '20px', height: '20px' }} />
                {t('emergency-call', 'Emergency', 'හදිසි')}
              </a>
            </div>

            {/* Footer */}
            <div style={{ padding: '16px', borderTop: '1px solid #e5e7eb', textAlign: 'center', backgroundColor: '#f9fafb' }}>
              <p style={{ fontSize: '14px', color: '#4b5563', margin: '0 0 4px 0' }}>
                {t('hospital-name', 'Divisional Hospital', 'ප්‍රාදේශීය රෝහල')}
              </p>
              <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>
                {t('location', 'Weeraketiya', 'වීරකැටිය')}
              </p>
            </div>
          </div>
        </>,
        document.body
      )}
    </header>
  );
};

export default Header;
